import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { SETTINGS_API } from '../config/settingsConfig';
import { type OrgSettings } from '../types/adminConfig';
import { getUrls } from '../utils/url';
import { transformSettingsData } from '../views/AdminSettings/utils/transformSettingsData';

// Fetch function for querying org settings
const fetchOrgSettings = async (dumpCache: boolean): Promise<OrgSettings> => {
    const { api } = getUrls();
    const url = new URL(SETTINGS_API, api);

    if (dumpCache) {
        url.searchParams.append('dumpCache', '1');
    }

    const { data: body } = await axios.get(url.toString());
    return transformSettingsData(body.data);
};

// Mutation function for updating settings
const updateOrgSettings = async (updatedSettings: OrgSettings): Promise<OrgSettings> => {
    const { api } = getUrls();
    const url = new URL(SETTINGS_API, api);

    const { data: body } = await axios.put(url.toString(), updatedSettings);
    return transformSettingsData(body.data);
};

// Custom Hook
export const useOrgSettings = (dumpCache: boolean = false) => {
    const queryClient = useQueryClient();

    // Fetch Settings
    const query = useQuery({
        queryKey: ['orgSettings', dumpCache],
        queryFn: () => fetchOrgSettings(dumpCache),
        staleTime: 1000 * 30,
        cacheTime: 1000 * 60,
        retry: 2,
        refetchOnWindowFocus: false
    });

    // Mutation Hook for Updating Settings
    const mutation = useMutation({
        mutationFn: updateOrgSettings,
        onSuccess: (updatedSettings) => {
            // Optimistically update the cache after mutation
            queryClient.setQueryData(['orgSettings', dumpCache], updatedSettings);
        }
    });

    return { ...query, updateSettingsState: mutation.mutate };
};
